/**
 * DOM utility helpers
 */

/**
 * Wait for an element to appear in the DOM
 */
export function waitForElement<T extends Element>(
  selector: string,
  timeout = 5000,
  container: Element | Document = document
): Promise<T | null> {
  return new Promise((resolve) => {
    const element = container.querySelector<T>(selector);
    if (element) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver(() => {
      const el = container.querySelector<T>(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeout);
  });
}

/**
 * Run callback when DOM is ready
 */
export function onDOMReady(callback: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

/**
 * Run callback when browser is idle (or after timeout)
 */
export function onIdle(callback: () => void, timeout = 2000): void {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 100);
  }
}

/**
 * Throttle a function using requestAnimationFrame
 */
export function rafThrottle<T extends (...args: unknown[]) => void>(
  fn: T
): (...args: Parameters<T>) => void {
  let ticking = false;
  return (...args: Parameters<T>) => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        fn(...args);
        ticking = false;
      });
      ticking = true;
    }
  };
}
